import { useEffect, useState } from "react";
import { X, Plus, Pencil, Trash2, Search, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { fmtMoney, fmtShortDate, cardCls, inputCls, labelCls } from "./accountingUtils";

const TERMS = [["due_on_receipt", "Due on receipt"], ["net_15", "Net 15"], ["net_30", "Net 30"], ["net_60", "Net 60"]];
export const ACCOUNT_TYPES = ["Bank", "Accounts receivable", "Other current assets", "Fixed assets", "Accounts payable", "Credit card", "Other current liabilities", "Long-term liabilities", "Equity", "Income", "Cost of goods sold", "Expenses", "Other income", "Other expense"];

/** Field configs per entity — mirrors QuickBooks' forms. */
export const ENTITY_CONFIG = {
  customers: {
    title: "Customers", singular: "customer", sortKey: "name",
    fields: [
      ["name", "Customer name"], ["company", "Company"], ["email", "Email", "email"], ["phone", "Phone"],
      ["address", "Billing address", "textarea"], ["terms", "Terms", "select", TERMS], ["taxable", "Taxable", "checkbox"], ["notes", "Notes", "textarea"],
    ],
    primary: (r) => r.name || r.company, secondary: (r) => [r.company !== r.name && r.company, r.email, r.phone].filter(Boolean).join(" · "),
  },
  suppliers: {
    title: "Suppliers", singular: "supplier",
    fields: [
      ["company", "Company"], ["name", "Contact name"], ["email", "Email", "email"], ["phone", "Phone"], ["address", "Address", "textarea"],
      ["account_no", "Account no."], ["business_no", "Business no. (GST/HST)"], ["default_category", "Default expense category"], ["terms", "Terms", "select", TERMS], ["notes", "Notes", "textarea"],
    ],
    primary: (r) => r.company || r.name, secondary: (r) => [r.name !== r.company && r.name, r.default_category, r.email].filter(Boolean).join(" · "),
  },
  items: {
    title: "Products & services", singular: "product or service",
    fields: [
      ["name", "Name"], ["sku", "SKU"], ["type", "Type", "select", [["service", "Service"], ["non_inventory", "Non-inventory"], ["inventory", "Inventory"], ["bundle", "Bundle"]]],
      ["category", "Category"], ["description", "Description (shows on sales forms)", "textarea"], ["price", "Sales price / rate", "number"], ["cost", "Cost", "number"],
      ["income_account", "Income account"], ["taxable", "Taxable", "checkbox"], ["qty_on_hand", "Quantity on hand (inventory)", "number"],
    ],
    primary: (r) => r.name, secondary: (r) => [r.sku, r.type?.replace("_", "-"), r.category].filter(Boolean).join(" · "), amount: (r) => r.price,
  },
  accounts: {
    title: "Chart of accounts", singular: "account", groupBy: "type",
    fields: [
      ["name", "Name"], ["type", "Account type", "select", ACCOUNT_TYPES.map((t) => [t, t])], ["detail_type", "Detail type"], ["number", "Account number"],
      ["description", "Description", "textarea"], ["opening_balance", "Opening balance", "number"],
    ],
    primary: (r) => r.name, secondary: (r) => [r.number, r.detail_type].filter(Boolean).join(" · "), amount: (r) => r.opening_balance,
  },
  mileage: {
    title: "Mileage", singular: "trip",
    fields: [
      ["date", "Date", "date"], ["start", "Start location"], ["end", "End location"], ["distance_km", "Distance (km)", "number"], ["round_trip", "Round trip", "checkbox"],
      ["purpose", "Purpose", "select", [["business", "Business"], ["personal", "Personal"]]], ["vehicle", "Vehicle"], ["rate", "Rate $/km (CRA 2026: 0.70)", "number"], ["notes", "Notes", "textarea"],
    ],
    primary: (r) => `${r.start || "Trip"} → ${r.end || ""}`, secondary: (r) => `${fmtShortDate(r.date)} · ${r.distance_km}${r.round_trip ? " ×2" : ""} km · ${r.purpose}${r.vehicle ? ` · ${r.vehicle}` : ""}`, amount: (r) => r.amount,
  },
};

const RecordDialog = ({ cfg, initial, onClose, onSave }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const f = {};
    cfg.fields.forEach(([k, , type, opts]) => { f[k] = initial?.[k] ?? (type === "checkbox" ? (k === "taxable") : type === "select" ? opts[0][0] : type === "date" ? new Date().toISOString().slice(0, 10) : ""); });
    setForm(f);
  }, [cfg, initial]);
  const save = async () => {
    setSaving(true);
    try { await onSave(form); onClose(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" data-testid="record-dialog">
      <div className="flex max-h-[92vh] w-full max-w-xl flex-col rounded-t-xl border border-white/10 bg-[#0b0b0f] text-white sm:rounded-lg">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <h3 className="text-base font-bold uppercase tracking-tight">{initial?.id ? "Edit" : "New"} {cfg.singular}</h3>
          <button type="button" onClick={onClose} data-testid="record-close" aria-label="Close" className="rounded-md p-1 text-white/60 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto px-4 py-4 sm:grid-cols-2">
          {cfg.fields.map(([k, label, type = "text", opts]) => (
            <div key={k} className={type === "textarea" ? "sm:col-span-2" : ""}>
              {type === "checkbox" ? (
                <label className="mt-5 flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={!!form[k]} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.checked }))} data-testid={`record-${k}`} className="h-4 w-4 accent-[#1a3fd1]" />{label}</label>
              ) : (
                <>
                  <label className={labelCls}>{label}</label>
                  {type === "select" ? (
                    <select value={form[k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} data-testid={`record-${k}`} className={`mt-1 ${inputCls} bg-[#0a0a0a]`}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                  ) : type === "textarea" ? (
                    <textarea rows={2} value={form[k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} data-testid={`record-${k}`} className={`mt-1 ${inputCls} h-auto py-2`} />
                  ) : (
                    <input type={type === "number" ? "text" : type} inputMode={type === "number" ? "decimal" : undefined} value={form[k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [k]: type === "number" ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value }))} data-testid={`record-${k}`} className={`mt-1 ${inputCls} ${type === "number" ? "font-mono" : ""}`} />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
          <button onClick={onClose} data-testid="record-cancel" className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="record-save" className="rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * AccountingRecords — generic QuickBooks-style list + form for customers,
 * suppliers, products & services, chart of accounts and mileage.
 */
const AccountingRecords = ({ base, entity, readOnly, viewUserId, onChanged }) => {
  const cfg = ENTITY_CONFIG[entity];
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null); // null | {initial}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`${base}/${entity}`, { params: { user_id: viewUserId || undefined } }); setRows(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [base, entity, viewUserId]);

  const save = async (form) => {
    if (dlg?.initial?.id) await api.put(`${base}/${entity}/${dlg.initial.id}`, form); else await api.post(`${base}/${entity}`, form);
    toast.success(`${cfg.singular[0].toUpperCase()}${cfg.singular.slice(1)} saved`);
    load(); onChanged?.();
  };
  const remove = async (r) => {
    if (!(await confirm({ title: `Delete this ${cfg.singular}?`, description: cfg.primary(r), confirmLabel: "Delete" }))) return;
    try { await api.delete(`${base}/${entity}/${r.id}`); toast.success("Deleted"); load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const shown = rows.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  const groups = cfg.groupBy ? [...new Set(shown.map((r) => r[cfg.groupBy]))].map((g) => ({ g, items: shown.filter((r) => r[cfg.groupBy] === g) })) : [{ g: null, items: shown }];
  const total = cfg.amount ? shown.reduce((s, r) => s + (Number(cfg.amount(r)) || 0), 0) : null;

  return (
    <div className="space-y-3" data-testid={`acct-${entity}`}>
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cfg.title.toLowerCase()}`} data-testid={`acct-${entity}-search`} className={`${inputCls} h-9 pl-8`} />
        </div>
        {!readOnly && <button type="button" onClick={() => setDlg({ initial: null })} data-testid={`acct-${entity}-new`} className="inline-flex h-9 items-center gap-1 rounded-md bg-[#1a3fd1] px-3 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85"><Plus className="h-3.5 w-3.5" /> New</button>}
      </div>
      <div className={`${cardCls} flex items-center justify-between`}>
        <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">{shown.length} {cfg.title.toLowerCase()}</span>
        {total != null && entity !== "accounts" && <span className="font-mono text-lg font-bold text-white">{fmtMoney(total)}</span>}
      </div>
      {loading ? <div className="flex justify-center py-10 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div> : shown.length === 0 ? (
        <div className={`${cardCls} py-10 text-center text-sm text-white/50`} data-testid={`acct-${entity}-empty`}>No {cfg.title.toLowerCase()} yet.</div>
      ) : groups.map(({ g, items }) => (
        <div key={g || "all"} className="overflow-hidden rounded-lg border border-white/10">
          {g && <div className="bg-white/[0.06] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white/70">{g}</div>}
          <ul className="divide-y divide-white/10 bg-white/[0.02]">
            {items.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`acct-${entity}-row-${r.id}`}>
                <button type="button" onClick={() => !readOnly && setDlg({ initial: r })} className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-semibold text-white">{cfg.primary(r)}</div>
                  <div className="truncate text-xs text-white/55">{cfg.secondary(r)}</div>
                </button>
                {cfg.amount && <span className="font-mono text-sm font-semibold text-white">{fmtMoney(cfg.amount(r))}</span>}
                {!readOnly && !r.system && (
                  <>
                    <button type="button" onClick={() => setDlg({ initial: r })} aria-label="Edit" className="hidden rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white sm:block"><Pencil className="h-4 w-4" /></button>
                    <button type="button" onClick={() => remove(r)} data-testid={`acct-${entity}-row-${r.id}-delete`} aria-label="Delete" className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {dlg && <RecordDialog cfg={cfg} initial={dlg.initial} onClose={() => setDlg(null)} onSave={save} />}
    </div>
  );
};

export default AccountingRecords;
